import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, faq, t } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines, useSignature } from '../../state/derived';
import { SB_THEMES, SKINS, type SkyblogTheme } from '../../state/types';
import {
  COMS_START,
  COMS_TICK_MS,
  comsStep,
  formatComs,
  secondArticleComs,
  topKikoos,
} from './coms';
import {
  DEFAULT_KIKOO_OPTIONS,
  KIKOO_TOGGLES,
  deptFor,
  formatDept,
  kikooName,
  MAX_KIKOO_BASE,
  normaliseBase,
  randomKikoo,
  type KikooOptions,
  type KikooToggle,
} from './kikoo';
import styles from './skyblog.module.css';

/** Libellé et pastilles de chaque thème, dans l'ordre de la boîte customise. */
const THEME_SWATCHES: Record<SkyblogTheme, readonly [string, string, string]> = {
  pink: ['#ff3ea5', '#8a2be2', '#ffe14d'],
  neon: ['#a855f7', '#4c1d95', '#22d3ee'],
  emo: ['#e11d48', '#3f3f46', '#a3a3a3'],
  steve: ['#f97316', '#0e4863', '#35c2c2'],
};

const THEME_LABELS: Record<SkyblogTheme, { readonly fr: string; readonly en: string }> = {
  pink: S.skyblog.customise.theme_pink,
  neon: S.skyblog.customise.theme_neon,
  emo: S.skyblog.customise.theme_emo,
  steve: S.skyblog.customise.theme_steve,
};

/** Libellé de chaque interrupteur du générateur. */
const TOGGLE_LABELS: Record<KikooToggle, { readonly fr: string; readonly en: string }> = {
  leet: S.skyblog.generator.opt_leet,
  alternate: S.skyblog.generator.opt_alternate,
  dept: S.skyblog.generator.opt_dept,
  mirror: S.skyblog.generator.opt_mirror,
  wrap: S.skyblog.generator.opt_wrap,
  glitter: S.skyblog.generator.opt_glitter,
};

/**
 * Le compteur de coms : part de 4021 et monte de 1 à 3 toutes les 4 secondes.
 *
 * Suspendu quand l'onglet est masqué — inutile de faire tourner un compteur que
 * personne ne regarde. Il n'est **pas mémorisé** : il repart de 4021 à chaque
 * visite, ce qui est plus drôle qu'un vrai compteur.
 */
function useComs(): number {
  const [coms, setComs] = useState(COMS_START);

  useEffect(() => {
    let id = 0;
    const start = (): void => {
      window.clearInterval(id);
      id = window.setInterval(() => {
        setComs((value) => value + comsStep());
      }, COMS_TICK_MS);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') start();
      else window.clearInterval(id);
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return coms;
}

/**
 * Le réflexe Skyblog : la musique part toute seule.
 *
 * Les navigateurs interdisent le vrai autoplay, alors on triche honnêtement —
 * la lecture démarre au **premier geste** du visiteur. Une seule fois par
 * visite, et seulement sur ce skin : les écouteurs sont posés par le skin
 * lui-même, ils partent avec lui.
 *
 * Un geste posé sur une commande ne compte pas. Sans ce filtre, un visiteur qui
 * clique sur « La chanson » voit les deux se déclencher : `pointerdown` lance la
 * lecture, puis le `click` du bouton la coupe — il demande la musique et obtient
 * le silence. C'est aussi ce que dit le lecteur : « clique n'importe où ».
 */
function useAutoplay(start: () => void): void {
  const startRef = useRef(start);
  startRef.current = start;

  useEffect(() => {
    let armed = true;

    const onControl = (target: EventTarget | null): boolean =>
      target instanceof Element && target.closest('button, a, input, label, select') !== null;

    const fire = (event: Event): void => {
      if (!armed || onControl(event.target)) return;
      armed = false;
      window.removeEventListener('pointerdown', fire);
      window.removeEventListener('keydown', fire);
      startRef.current();
    };

    window.addEventListener('pointerdown', fire);
    window.addEventListener('keydown', fire);
    return () => {
      armed = false;
      window.removeEventListener('pointerdown', fire);
      window.removeEventListener('keydown', fire);
    };
  }, []);
}

/** Skin 8 — Skyblog : le blog d'un fan, en 2005, avec l'orthographe d'époque. */
export function SkyblogSkin(): ReactNode {
  const { lang, seen, draft, playing, sbTheme, kikoo, audioMissing } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();
  const signature = useSignature();
  const questions = faq(lang);

  const coms = useComs();
  const [friend, setFriend] = useState(false);
  useAutoplay(actions.toggleAudio);

  /*
   * Le générateur ne vit que le temps de la visite du skin : c'est le pseudo
   * *adopté* qui est mémorisé, pas la recette qui l'a produit. Un visiteur qui
   * revient retrouve sa signature, pas les six interrupteurs dans leur position.
   */
  const [base, setBase] = useState('');
  const [options, setOptions] = useState<KikooOptions>(DEFAULT_KIKOO_OPTIONS);
  const preview = kikooName(base, options);

  const marquee = t(S.skyblog.header.marquee, lang);

  return (
    <div className={styles.root} data-sb-theme={sbTheme}>
      {/* Décorative : son texte est repris par le titre et le sous-titre. */}
      <div className={styles.marquee} aria-hidden="true">
        <div className={styles.marqueeTrack}>
          <span className={styles.marqueeCopy}>{marquee}</span>
          <span className={styles.marqueeCopy}>{marquee}</span>
        </div>
      </div>

      <header className={styles.header}>
        <h1 className={styles.title}>{t(S.skyblog.header.title, lang)}</h1>
        <p className={styles.tagline}>{t(S.skyblog.header.tagline, lang)}</p>
      </header>

      <div className={styles.grid}>
        <div className={styles.profileColumn}>
          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.profile.box_title, lang)}</h2>
            <div className={styles.boxBody}>
              <div className={styles.avatarFrame}>
                {/*
                  L'échelle sur le wrapper, `bob` sur le dessin : les deux sur
                  le même élément et l'échelle disparaîtrait.
                */}
                <div className={styles.avatarScale}>
                  <Steve bobDuration="3s" onClick={actions.bump} label={t(S.common.title, lang)} />
                </div>
              </div>
              <p className={styles.identity}>{t(S.skyblog.profile.identity, lang)}</p>
              <p className={styles.bio}>
                {t(S.skyblog.profile.bio_1, lang)}
                <br />
                {t(S.skyblog.profile.bio_2, lang)}
              </p>
              <div className={styles.buttons}>
                <button
                  type="button"
                  className={styles.button}
                  aria-pressed={friend}
                  onClick={() => {
                    setFriend(true);
                    actions.praise();
                  }}
                >
                  {t(friend ? S.skyblog.profile.added : S.skyblog.profile.add_friend, lang)}
                </button>
                <button type="button" className={styles.button} onClick={actions.toggleAudio}>
                  {audio.label}
                </button>
              </div>
              <p className={styles.hint} role="status">
                {audio.hint}
              </p>
            </div>
          </section>

          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.customise.box_title, lang)}</h2>
            <div className={styles.boxBody}>
              {SB_THEMES.map((theme) => (
                <button
                  key={theme}
                  type="button"
                  className={cx(styles.themeButton, sbTheme === theme && styles.themeActive)}
                  aria-pressed={sbTheme === theme}
                  onClick={() => {
                    actions.setSkyblogTheme(theme);
                  }}
                >
                  <span className={styles.swatches} aria-hidden="true">
                    {THEME_SWATCHES[theme].map((color) => (
                      <span key={color} className={styles.swatch} style={{ background: color }} />
                    ))}
                  </span>
                  <span>{t(THEME_LABELS[theme], lang)}</span>
                </button>
              ))}
            </div>
          </section>

          {/*
            Le générateur de pseudo. C'est la seule boîte du skin dont l'effet
            sort du skin : le pseudo adopté signe les messages du visiteur sur
            les onze interfaces, à la place de `visiteur_anonyme`.
          */}
          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.generator.box_title, lang)}</h2>
            <div className={styles.boxBody}>
              <label className={styles.genLabel} htmlFor="sb-kikoo-base">
                {t(S.skyblog.generator.field_label, lang)}
              </label>
              <input
                id="sb-kikoo-base"
                type="text"
                className={styles.input}
                value={base}
                maxLength={MAX_KIKOO_BASE * 2}
                placeholder={t(S.skyblog.generator.placeholder, lang)}
                onChange={(event) => {
                  setBase(event.currentTarget.value);
                }}
              />

              <div className={styles.genToggles}>
                {KIKOO_TOGGLES.map((toggle) => (
                  <button
                    key={toggle}
                    type="button"
                    className={cx(styles.genToggle, options[toggle] && styles.genToggleOn)}
                    aria-pressed={options[toggle]}
                    onClick={() => {
                      setOptions((current) => ({ ...current, [toggle]: !current[toggle] }));
                    }}
                  >
                    {t(TOGGLE_LABELS[toggle], lang)}
                    {/*
                      Le département n'est pas demandé, il est déduit du blaze :
                      autant le montrer sur l'interrupteur qui le pose.
                    */}
                    {toggle === 'dept' && normaliseBase(base) !== '' ? (
                      <span className={styles.genDept}>
                        {formatDept(deptFor(normaliseBase(base)))}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>

              <p className={styles.genPreviewLabel}>
                {t(S.skyblog.generator.preview_label, lang)}
              </p>
              {/* La prévisualisation est en WordArt : c'est déjà une signature. */}
              <output
                className={cx(styles.genPreview, preview === '' && styles.genPreviewEmpty)}
                htmlFor="sb-kikoo-base"
              >
                {preview === '' ? t(S.skyblog.generator.preview_empty, lang) : preview}
              </output>

              <div className={styles.buttons}>
                <button
                  type="button"
                  className={styles.button}
                  disabled={preview === '' || preview === kikoo}
                  onClick={() => {
                    actions.setKikoo(preview);
                  }}
                >
                  {t(
                    preview !== '' && preview === kikoo
                      ? S.skyblog.generator.already
                      : S.skyblog.generator.adopt,
                    lang,
                  )}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    const drawn = randomKikoo();
                    setBase(drawn.base);
                    setOptions(drawn.options);
                  }}
                >
                  {t(S.skyblog.generator.random, lang)}
                </button>
                {kikoo === '' ? null : (
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => {
                      actions.setKikoo('');
                    }}
                  >
                    {t(S.skyblog.generator.drop, lang)}
                  </button>
                )}
              </div>

              <p className={styles.genSigned} role="status">
                {t(
                  kikoo === '' ? S.skyblog.generator.signed_none : S.skyblog.generator.signed,
                  lang,
                  { name: signature, n: SKINS.length },
                )}
              </p>
            </div>
          </section>

          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.kikoos.box_title, lang)}</h2>
            <div className={styles.boxBody}>
              <ol className={styles.kikoos}>
                {topKikoos(coms).map((kikoo, index) => (
                  <li key={kikoo.name} className={styles.kikoo}>
                    <span className={styles.rank} aria-hidden="true">
                      {index + 1}
                    </span>
                    <span className={styles.kikooName}>{kikoo.name}</span>
                    <span className={styles.kikooComs}>
                      {t(S.skyblog.kikoos.coms_suffix, lang, { n: formatComs(kikoo.coms, lang) })}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.visits.box_title, lang)}</h2>
            <div className={styles.boxBody}>
              <p className={styles.visitsLabel}>{t(S.skyblog.visits.label, lang)}</p>
              {/* Six chiffres, comme tous les compteurs de l'époque. */}
              <div className={styles.counter}>{String(seen).padStart(6, '0')}</div>
              <p className={styles.visitsSub}>{t(S.skyblog.visits.sub, lang)}</p>
            </div>
          </section>
        </div>

        <div className={styles.articleColumn}>
          <article className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.articles.a1_title, lang)}</h2>
            <div className={styles.boxBody}>
              <p className={styles.meta}>
                {t(S.skyblog.articles.a1_meta, lang, { n: formatComs(coms, lang) })}
              </p>
              <p className={styles.paragraph}>{t(S.y2k.who_p1, lang)}</p>
              <p className={styles.paragraph}>
                <AuthorText linkClassName={styles.creditLink}>
                  {t(S.skyblog.articles.a1_p2, lang)}
                </AuthorText>
              </p>
              <p className={styles.cta}>{t(S.skyblog.articles.a1_cta, lang)}</p>
            </div>
          </article>

          <article className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.articles.a2_title, lang)}</h2>
            <div className={styles.boxBody}>
              <p className={styles.meta}>
                {t(S.skyblog.articles.a2_meta, lang, {
                  n: formatComs(secondArticleComs(coms), lang),
                })}
              </p>

              <VuMeter className={styles.vu} barClassName={styles.vuBar} />

              <div className={styles.pads}>
                {mixer.pads.map((pad) => (
                  <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                    <span>{pad.label}</span>
                    <span className={styles.padTime}>{pad.timeLabel}</span>
                  </button>
                ))}
              </div>

              <div className={styles.buttons}>
                <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
                  {mixer.holdLabel}
                </button>
                <button type="button" className={styles.button} onClick={mixer.onStop}>
                  {t(S.skyblog.mixer.stop, lang)}
                </button>
              </div>

              {mixer.sliders.map((slider) => (
                <div key={slider.key} className={styles.slider}>
                  <label className={styles.sliderLabel} htmlFor={`sb-${slider.key}`}>
                    {slider.label}
                  </label>
                  <input
                    id={`sb-${slider.key}`}
                    type="range"
                    className={styles.sliderInput}
                    min={slider.min}
                    max={slider.max}
                    step={slider.step}
                    value={slider.value}
                    onChange={(event) => {
                      slider.onChange(event.currentTarget.valueAsNumber);
                    }}
                  />
                  <output className={styles.sliderValue} htmlFor={`sb-${slider.key}`}>
                    {slider.valueLabel}
                  </output>
                </div>
              ))}

              <div className={styles.buttons}>
                {mixer.presets.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    className={styles.button}
                    onClick={preset.onSelect}
                  >
                    {preset.label}
                  </button>
                ))}
                <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
                  {mixer.wobbleLabel}
                </button>
              </div>
            </div>
          </article>

          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.articles.faq_title, lang)}</h2>
            <div className={styles.boxBody}>
              <dl className={styles.faq}>
                {questions.map((item) => (
                  <div key={item.question}>
                    <dt className={styles.question}>{item.question}</dt>
                    <dd className={styles.answer}>{item.answer}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          <section className={styles.box}>
            <h2 className={styles.boxHead}>{t(S.skyblog.articles.coms_title, lang)}</h2>
            <div className={styles.boxBody}>
              <ul className={styles.entries}>
                {guestbook.map((entry) => (
                  <li key={entry.id} className={styles.entry}>
                    <span className={styles.entryWho}>{entry.who}</span>{' '}
                    <span className={styles.entryWrote}>{t(S.skyblog.guestbook.wrote, lang)}</span>{' '}
                    {entry.text}
                  </li>
                ))}
              </ul>
              <div className={styles.compose}>
                <input
                  type="text"
                  className={styles.input}
                  value={draft}
                  placeholder={t(S.skyblog.guestbook.placeholder, lang)}
                  aria-label={t(S.guestbook.placeholder, lang)}
                  onChange={(event) => {
                    actions.setDraft(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') actions.postEntry();
                  }}
                />
                <button type="button" className={styles.button} onClick={actions.postEntry}>
                  {t(S.skyblog.guestbook.send, lang)}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <footer className={styles.credits}>
        <Credits linkClassName={styles.creditLink} unofficial />
        <span>{t(S.common.egg_hint, lang)}</span>
      </footer>

      <div className={styles.player}>
        <span className={styles.playerTitle}>
          ✦ {t(playing ? S.skyblog.player.playing : S.skyblog.player.paused, lang)}
        </span>
        <span
          className={styles.progress}
          aria-hidden="true"
          title={t(S.skyblog.player.progress, lang)}
        />
        <span className={styles.playerNote}>
          {t(
            audioMissing
              ? S.skyblog.player.note_missing
              : playing
                ? S.skyblog.player.note_playing
                : S.skyblog.player.note_idle,
            lang,
          )}
        </span>
        {/* Minuscule à l'œil, 44 px au doigt : la blague est visuelle. */}
        <button type="button" className={styles.stop} onClick={mixer.onStop}>
          {t(S.skyblog.player.stop, lang)}
        </button>
      </div>
    </div>
  );
}
